import { useMemo, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/modern/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/modern/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { SURFACE_CONTROL_CLS } from "@/react/components/entityViews/files/constants/surfaceChrome";
import { cn } from "@/modern/utils";
import {
  ArrowDownWideNarrow,
  ArrowUpDown,
  Check,
  Clock3,
  type LucideIcon,
} from "lucide-react";
import {
  type FileBrowserFilterState,
  type FileListOrdering,
} from "./types";

interface SortPopoverProps {
  ordering: FileListOrdering;
  onChange: (partial: Partial<FileBrowserFilterState>) => void;
}

const SORT_OPTIONS: { value: FileListOrdering; label: string; icon: LucideIcon }[] = [
  { value: "-uploaded_at", label: "Newest", icon: Clock3 },
  { value: "uploaded_at", label: "Oldest", icon: Clock3 },
  { value: "-updated_at", label: "Recently updated", icon: ArrowDownWideNarrow },
];

/**
 * Sort control — always shows the active sort label in the trigger (including Newest).
 */
export function SortPopover({ ordering, onChange }: SortPopoverProps) {
  const [open, setOpen] = useState(false);
  const activeLabel = useMemo(
    () => SORT_OPTIONS.find((o) => o.value === ordering)?.label ?? "Sort",
    [ordering],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Sort: ${activeLabel}`}
              className={cn(
                "inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-full px-3.5 text-sm font-normal",
                "transition-[transform,background-color,color,border-color] duration-150 ease-out",
                "hover:border-input-enhanced hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "active:scale-[0.98]",
                SURFACE_CONTROL_CLS,
                "border-primary/40 text-foreground",
              )}
            >
              <ArrowUpDown className="size-3.5 shrink-0" aria-hidden />
              <span className="max-w-[9rem] truncate">{activeLabel}</span>
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">Sort: {activeLabel}</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-[18rem] overflow-hidden rounded-xl border-border/60 p-0 shadow-xl sm:w-[20rem]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command className="rounded-none bg-transparent">
          <CommandList className="max-h-[min(16rem,40vh)] p-1.5">
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
              No sort options.
            </CommandEmpty>
            <CommandGroup heading="Sort by">
              {SORT_OPTIONS.map((opt) => {
                const active = ordering === opt.value;
                const Icon = opt.icon;
                return (
                  <CommandItem
                    key={`sort-${opt.value}`}
                    value={`sort ${opt.label}`}
                    onSelect={() => {
                      onChange({ ordering: opt.value });
                      setOpen(false);
                    }}
                    className="mx-0 gap-2 rounded-lg px-2.5 py-2 text-sm font-normal"
                  >
                    <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="flex-1 truncate">{opt.label}</span>
                    {active ? (
                      <Check className="size-3.5 shrink-0 text-foreground" aria-hidden />
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
